import { isApplePlatform } from '@client/utils/common/platform';
import { cn } from '@client/utils/styling/cn';
import type { ReactNode } from 'react';

/**
 * Platform-aware key aliases. `Mod` renders as ⌘ on Apple hardware and Ctrl elsewhere, so a single
 * shortcut definition can be shared between the handler and every place it is advertised.
 */
const KEY_ALIASES: Record<string, { mac: string; other: string }> = {
    Mod: { mac: '⌘', other: 'Ctrl' },
    Meta: { mac: '⌘', other: 'Win' },
    Alt: { mac: '⌥', other: 'Alt' },
    Shift: { mac: '⇧', other: 'Shift' },
    Enter: { mac: '⏎', other: 'Enter' },
    Escape: { mac: 'Esc', other: 'Esc' },
    ArrowLeft: { mac: '←', other: '←' },
    ArrowRight: { mac: '→', other: '→' },
    ArrowUp: { mac: '↑', other: '↑' },
    ArrowDown: { mac: '↓', other: '↓' },
};

export function resolveKeyLabel(key: string): string {
    const alias = KEY_ALIASES[key];
    if (!alias) return key;
    return isApplePlatform() ? alias.mac : alias.other;
}

/**
 * A single keyboard key rendered as a physical-looking cap.
 *
 * Use wherever a shortcut is advertised in the interface — inside a button ("Submit review ⌘⏎"),
 * in a footer hint, in a shortcut cheat-sheet. Advertising the shortcut where the action lives is
 * what turns a power feature into a discoverable one; a shortcut only documented in a popover is a
 * shortcut nobody learns.
 *
 * Pass a raw character (`<Kbd>J</Kbd>`) or an alias that should adapt to the platform
 * (`<Kbd>Mod</Kbd>` → ⌘ / Ctrl).
 *
 * @example
 * <Button type="primary">Submit review <KbdKeys keys={['Mod', 'Enter']} /></Button>
 */
export function Kbd({
    children,
    tone = 'default',
    className,
}: {
    children: string;
    /**
     * `default` for keys on a page/card surface; `inverse` for keys sitting inside a filled
     * primary button, where the neutral cap would disappear into the gradient.
     */
    tone?: 'default' | 'inverse';
    className?: string;
}) {
    return (
        <kbd
            className={cn(
                'inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-xs border px-1 font-medium font-mono text-2xs leading-none',
                // Both tones are measured against their real surface at 11px, which needs the full
                // 4.5:1. `inverse` DARKENS its cap rather than lightening it: a white veil over the
                // accent gradient lifts the fill toward the white glyph (3.85:1 at the accent-500
                // end), where a black veil pushes away from it (6.97:1). `default` uses
                // muted-foreground, not subtle-foreground — the latter is 4.39:1 on `bg-muted`,
                // which clears AA on white but not on the chip's own tinted fill.
                tone === 'inverse'
                    ? 'border-white/40 bg-black/15 text-white'
                    : 'border-separator bg-muted text-muted-foreground',
                className,
            )}
        >
            {resolveKeyLabel(children)}
        </kbd>
    );
}

/**
 * A chord or sequence of keys — `['Mod', 'Enter']` renders as ⌘ ⏎.
 *
 * Set `separator` to show an explicit joiner (a `+` for chords in a cheat-sheet); the default is a
 * bare gap, which reads better inline inside a button label.
 */
export function KbdKeys({
    keys,
    separator,
    tone,
    className,
}: {
    keys: string[];
    separator?: ReactNode;
    tone?: 'default' | 'inverse';
    className?: string;
}) {
    return (
        <span className={cn('inline-flex items-center gap-0.5', className)}>
            {keys.map((key, index) => (
                <span key={`${key}-${index}`} className="inline-flex items-center gap-0.5">
                    <Kbd tone={tone}>{key}</Kbd>
                    {separator && index < keys.length - 1 ? (
                        <span className="text-subtle-foreground text-xs">{separator}</span>
                    ) : null}
                </span>
            ))}
        </span>
    );
}
